import type {
  DataSettings,
  EntityID,
  Grid,
  PointCloudGradient,
} from "./three-d-schemas";

export {
  CAMERA_TYPE,
  entityDeclarationSchema,
  entityRegistrySchema,
  axisAngleRotationSchema,
  dataSettingsSchema,
  eulerRotationSchema,
  jointBindingSchema,
  jointMimicSchema,
  jointSchema,
  jointValueSchema,
  meshSchema,
  modelMeshSchema,
  modelNodeMeshSchema,
  orbitTargetKindSchema,
  pathSchema,
  pointCloudAxisSchema,
  pointCloudColorModeSchema,
  pointCloudGradientMeasureSchema,
  pointCloudGradientSchema,
  pointCloudSchema,
  quaternionRotationSchema,
  rotationSchema,
  positionBasePresetSchema,
  positionTransformationSchema,
  threeDPanelConfigSchema,
  threeDPanelDataSchema,
  vectorFieldSchema,
  type Camera,
  type OrbitCamera,
  type OrbitTargetKind,
  type ThirdPersonCamera,
  type PanelData,
  type PanelConfig,
  type Path,
  type PositionTransformation,
  type PositionBasePreset,
  type Position,
  type Sphere,
  type Box,
  type EntityDeclaration,
  type EntityRegistry,
  type AxisAngleRotation,
  type EulerRotation,
  type Joint,
  type JointBinding,
  type JointMimic,
  type JointValue,
  type Mesh,
  type ModelMesh,
  type ModelNodeMesh,
  type PointCloud,
  type PointCloudAxis,
  type PointCloudColorMode,
  type PointCloudGradient,
  type PointCloudGradientMeasure,
  type QuaternionRotation,
  type Rotation,
  type VectorFieldDisplayMode,
  type VectorField,
  type Grid,
  type DataSettings,
  type Entity,
  type EntityID,
} from "./three-d-schemas";

export { deriveSourceFileIdFromAxes, type Axes } from "./axesFile";

export {
  bodyConventionFor,
  CONVENTIONS,
  conventionIdSchema,
  determinantOf,
  toScene,
  type Convention,
  type ConventionAxis,
  type ConventionId,
  type SourceAxis,
} from "./conventions";

export {
  clampToLimits,
  evaluateJointValues,
  mimicOrder,
  type MimicOrder,
} from "./jointMimic";

export {
  ancestorChain,
  buildEntityTree,
  descendantIds,
  findParentCycle,
  resolveParent,
  topologicalOrder,
  type EntityTreeNode,
  type HierarchyEntity,
} from "./entityHierarchy";

export const DEFAULT_ENTITY_ID: EntityID = "entity0";

/**
 * The floor grid a 3D panel starts with: five-metre cells, in a mid grey with
 * the alpha that keeps it a reference rather than a wall.
 *
 * Named here, rather than written into the reducer that creates a panel, so that
 * everything which opens a 3D scene without a saved grid to restore — the panel
 * itself, and the ingest wizard's preview — starts from the same floor. Two
 * literals that happened to agree would be two literals free to stop agreeing.
 *
 * `verticalOffset` is left out: the renderer derives the grid's height from the
 * first positioned entity when none is given. A consumer that needs the key
 * present — `lodash.isEqual` counts absent and `undefined` as different — should
 * spread this and add it explicitly.
 */
export const DEFAULT_GRID: Grid = { color: "#88888888", scale: 5 };

/**
 * What each data setting means when a panel's `dataSettings` leaves it unset:
 * how the panel read its data before the setting existed.
 *
 * Named here, beside `DEFAULT_GRID`, because the renderer resolves an unset
 * field from it and the settings menu shows an unset field from it; the two
 * must agree, or the menu would describe a panel that draws differently.
 */
export const DEFAULT_DATA_SETTINGS: Required<DataSettings> = {
  interpolationEnabled: true,
  interpolationThresholdHz: 0,
};

/**
 * What a point cloud in the "gradient" color mode is colored by when its spec
 * has no `gradient`: height, blue at the shown message's lowest point and red
 * at its highest.
 *
 * Named here, beside `DEFAULT_GRID`, because the renderer draws a spec without
 * a gradient this way and the settings start a new gradient from it; the two
 * must agree, or a spec the settings leave minimal would draw differently from
 * what the settings show.
 */
export const DEFAULT_POINT_CLOUD_GRADIENT: PointCloudGradient = {
  by: "up",
  colorLow: "#0000ff",
  colorHigh: "#ff0000",
};
